import React, { useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"

import API from "api"

import LoadingText from "components/loadingText"
import { LOCALES } from "constants/locales"
import { APP_LOCALE, getLanguage, useLanguage } from "@languages/index"


const MatchRatingEditorWithSteps = (props: {
  [key:string]:any,
  containerClassName?: string,
  step: number,
  onChangeStyles: (styles:any) => void,
  onChangeCategories: (categories:any) => void,
  onLoaded: (isLoaded:boolean) => void,
}) => {

  /*********************************************************
   * LANGUAGES
   *********************************************************/

  const currentLocale = getLanguage()
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.matchRatingsEditor }



  /*********************************************************
   * STATES
   *********************************************************/

  const [styles, setStyles] = useState([]);
  const [categories, setCategories] = useState([]);

  const isFetchingRef = useRef(true);
  const [isFetching, setIsFetchingState] = useState(isFetchingRef.current);



  /********************************************************************
   **  STATE FUNCTIONS
   ********************************************************************/

  const setIsFetching = (newValue:boolean) => {
    isFetchingRef.current = newValue;
    setIsFetchingState(newValue);
    props.onLoaded(!newValue);
  }



  /********************************************************************
   **  useEffects
   ********************************************************************/

  useEffect(() => {
    Promise.all([
      fetchStyles(),
      fetchCategories(),
      fetchMatchRatings(),
    ]).then(([styles, categories, matchRatings]) => {
      for (let style of styles) {
        for (let matchRating of matchRatings) {
          if (style.id === matchRating.styleId) {
            style.rating = matchRating.rating;
          }
        }
      }
      sortStyles(styles);

      for (let category of categories) {
        for (let matchRating of matchRatings) {
          if (category.id === matchRating.categoryId) {
            category.rating = matchRating.rating;
          }
        }
      }

      setStyles(styles);
      setCategories(categories);

      props.onChangeStyles(styles);
      props.onChangeCategories(categories);

      setIsFetching(false);
    }).catch((error) => {
      console.error('[error]', error);
      props.showModal({
        title: '[Error] Fetching Styles & Categories',
        text: error.message,
      });
    });
  }, []);



  /********************************************************************
   **  REQUESTS
   ********************************************************************/
  
   const fetchStyles = async () => {
    const response = await API.Style.index({
      isUseForMatch: true,
    });
    return response.data.styles;
  }


  const fetchCategories = async () => {
    const response = await API.Category.index({
      isUseForMatch: true,
      isMatchSettable: true,
    });
    return response.data.categories;
  }
  

  const fetchMatchRatings = async () => {
    const response = await API.MatchRating.index({
      brandToken: props.auth.token,
      brandId: props.brand.id,
    });
    return response.data.matchRatings;
  }



  /********************************************************************
   **  HELPER FUNCTIONS
   ********************************************************************/

   function parseStyleCategoryLocaleName (styleOrCategory):string {
    let name = '';
    switch (currentLocale) {
      case LOCALES.KO:
        name = styleOrCategory.nameKo;
        break;
      case LOCALES.JA:
        name = styleOrCategory.nameJa;
        break;
      default:
        name = styleOrCategory.name;
        break;
    }
    return name;
  }


  function sortStyles (styles) {
    styles.sort((a, b) => {
      if (b.rating > a.rating || (b.rating != null && a.rating == null))
        return 1;
      if ((b.rating == null && a.rating == null) || b.rating == a.rating)
        return 0;
      return -1;
    });
  }


  function recursiveAddOne (styles, originalRating) {
    const hasSpace = styles.find((s, i) => s.rating === originalRating + 1) == null;
    if (!hasSpace) {
      recursiveAddOne(styles, originalRating + 1)
    }
    for (let s of styles) {
      if (s.rating === originalRating) {
        s.rating += 1;
      }
    }
  }


  function moveStyleToZeroBoundary (styles, idx) {
    const spliced = styles.splice(idx, 1);
    let boundaryIdx = 0;
    for (let i = 0; i < styles.length; i++) {
      if (styles[i].rating == null || styles[i].rating === 0) {
        boundaryIdx = i;
        break;
      }
    }
    styles.splice(boundaryIdx, 0, ...spliced);
  }

  

  /********************************************************************
   **  EVENT HANDLERS
   ********************************************************************/

  const handleStyleRatingClick = useCallback(async (style, idx) => {
    // UNRATE
    if (style.rating != null && style.rating > 0) {
      style.rating = 0;
      for (let s of styles) {
        if (s.rating == null) {
          s.rating = 0;
        }
      }


    // RATE
    } else {
      let hasRatingOne = false;
      for (let s of styles) {
        if (s.rating === 1) {
          hasRatingOne = true;
          break;
        }
      }
      if (hasRatingOne) {
        recursiveAddOne(styles, 1);
      }
      style.rating = 1;
    }

    moveStyleToZeroBoundary(styles, idx);
    setStyles([...styles]);
    props.onChangeStyles([...styles])
  }, [styles]);


  const handleCategoryRatingClick = useCallback(async (category, ratingOfSelected:number) => {
    // UNRATE
    if (category.rating != null && category.rating === ratingOfSelected) {
      category.rating = 0;

    // RATE
    } else {
      category.rating = ratingOfSelected;
    }

    setCategories([...categories]);
    props.onChangeCategories([...categories])
  }, [categories]);



  /********************************************************************
   **  RENDERERS
   ********************************************************************/

  const StyleCategoryButton = ({
    rank,
    name,
    rating,
    isRatingTagVisible,
    onClick,
  }: {
    rank:number,
    name:string,
    rating:number,
    isRatingTagVisible:boolean,
    onClick:Function,
  }) => {
    let isSelected = rating != null && rating !== 0;

    return (
      <div
        className={classNames([
          "relative border select-none cursor-pointer",
          isSelected ? "border-black" : "border-[#E8E8E8]"
        ])}
        onClick={(e) => onClick()}
      >
        {isRatingTagVisible && isSelected && (
          <div className="absolute top-0 left-0 px-[5px] py-[2px] text-[10px] text-white bg-black">
            {rank}
          </div>
        )}
        <div className="px-[18px] py-[13px] text-[15px]">{name}</div>
      </div>
    )
  }

  return (
    <div
      className={classNames(["relative", props.containerClassName])}
    >
      {isFetching && (
        <div
          className="h-[200px] flex items-center justify-center rounded-md bg-slate-100"
        >
          <LoadingText
            text={"Loading"}
            render={(text) => (
              <div className="w-[180px] rounded-full p-2 text-center text-xs text-slate-600 select-none">
                {text}
              </div>
            )}
          />
        </div>
      )}

      {!isFetching && (
        <div className={classNames(["flex flex-col gap-[50px] select-none"])}>

          {/* STYLES */}
          {props.step === 0 && (
            <div className="flex flex-wrap gap-[6px]">
              {styles.map((style, idx) => {
                return <StyleCategoryButton
                  key={style.id}
                  rank={idx + 1}
                  name={parseStyleCategoryLocaleName(style)}
                  rating={style.rating ?? 0}
                  isRatingTagVisible={true}
                  onClick={() => handleStyleRatingClick(style, idx)}
                />
              })}
            </div>
          )}

          {/* POSITIVE CATEGORIES */}
          {props.step === 1 && (
            <div className="flex flex-wrap gap-[6px]">
              {categories.map((category, idx) => {
                return <StyleCategoryButton
                  key={category.id}
                  rank={idx + 1}
                  name={parseStyleCategoryLocaleName(category)}
                  rating={(category.rating != null && category.rating >= 0) ? category.rating : 0}
                  isRatingTagVisible={false}
                  onClick={() => handleCategoryRatingClick(category, 1)}
                />
              })}
            </div>
          )}

          {/* NEGATIVE CATEGORIES */}
          {props.step === 2 && (
            <div className="flex flex-wrap gap-[6px]">
              {categories.map((category, idx) => {
                return <StyleCategoryButton
                  key={category.id}
                  rank={idx + 1}
                  name={parseStyleCategoryLocaleName(category)}
                  rating={(category.rating != null && category.rating <= 0) ? category.rating : 0}
                  isRatingTagVisible={false}
                  onClick={() => handleCategoryRatingClick(category, -1)}
                />
              })}
            </div>
          )}
        </div>
      )}

    </div>
  )
}

export default MatchRatingEditorWithSteps
